import {
  Component,
  forwardRef,
  Input,
  inject,
  ChangeDetectorRef, booleanAttribute
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';

/**
 * Toggle component that implements ControlValueAccessor for form integration.
 *
 * This component provides a toggle switch/checkbox UI element that can be used in Angular forms.
 * It supports reactive forms and template-driven forms through the ControlValueAccessor interface.
 *
 * @example
 * <app-toggle [(ngModel)]="isEnabled"></app-toggle>
 *
 * @example
 * <app-toggle [formControl]="toggleControl"></app-toggle>
 */
@Component({
  selector: 'app-toggle',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './toggle.component.html',
  styleUrls: ['./toggle.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ToggleComponent),
      multi: true
    }
  ]
})
export class ToggleComponent implements ControlValueAccessor {
  /**
   * Reference to the change detector for managing component state updates.
   */
  protected readonly _changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);

  /**
   * Determines if the toggle is disabled.
   * When disabled, the toggle cannot be interacted with and appears visually dimmed.
   * @default false
   */
  @Input() public disabled: boolean = false;

  /**
   * Optional name attribute for the underlying input element.
   * Useful when using the toggle within forms that need named inputs.
   */
  @Input() public name?: string;

  /**
   * Optional ID attribute for the underlying input element.
   * Useful for associating a label with this toggle component.
   */
  @Input() public id?: string;

  /**
   * Optional aria-label attribute for accessibility.
   * Provides a text alternative for screen readers when no visible label is available.
   */
  @Input('aria-label') public ariaLabel?: string;

  /**
   * Controls the checked state of the toggle.
   * Can be used for two-way binding with [(checked)].
   * The booleanAttribute transform ensures the input is treated as a boolean.
   */
  @Input({ transform: booleanAttribute })
  get checked(): boolean {
    return this._checked;
  }
  set checked(value: boolean) {
    this._checked = value;
    this._changeDetectorRef.markForCheck();
  }



  /** Internal Value **/
  private _checked: boolean = false;

  /** Necessary FormControls **/
  private onTouched: () => void = () => {};
  private onChange: (_: boolean) => void = () => {};

  /**
   * Implements ControlValueAccessor.writeValue.
   * Called by Angular forms when the form model value changes.
   * Updates the internal component state to match the new value.
   *
   * @param value - The new value to write to the component
   */
  public writeValue(value: any): void {
    if (value !== this._checked) {
      this._checked = value;

      this.checked = this._checked;
    }
  }

  /**
   * Implements ControlValueAccessor.registerOnChange.
   * Registers a callback function to be invoked when the toggle value changes.
   *
   * @param fn - Callback function that will be triggered on value change
   */
  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  /**
   * Implements ControlValueAccessor.registerOnTouched.
   * Registers a callback function to be invoked when the toggle is touched.
   *
   * @param fn - Callback function that will be triggered on touch/blur
   */
  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  /**
   * Implements ControlValueAccessor.setDisabledState.
   * Called when the disabled state of the control changes.
   *
   * @param isDisabled - Whether the control should be disabled
   */
  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  /**
   * Toggles the checked state of the component.
   * Does nothing if the component is disabled.
   * Notifies the form of the value change through the onChange callback.
   */
  public toggle(): void {
    if (this.disabled) return;
    this.checked = !this.checked;

    this.onChange(this.checked)
  }

  /**
   * Handles the blur event on the component.
   * Triggers the onTouched callback to notify the form that the field was touched.
   */
  public onBlur(): void {
    this.onTouched();
  }

}
